/**
 * Support Stage — Stage 5: Support/hint validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.6
 *
 * Validates:
 * - Families that require hints have them available           [domain: support]
 * - Support coverage meets policy requirements                 [domain: support]
 * - defaultExplanation quality (presence, length) — registry field [domain: support]
 *   (rolled up under SUPPORT_EXPLANATION_QUALITY with metadata.reason)
 * - Family question count                                      [domain: familyStats]
 * - citoDifficulty spread within family                        [domain: familyStats]
 *   (rolled up under SUPPORT_FAMILY_CITO_SPREAD with metadata.reason)
 * - Number pattern collapse (numeric-content modules)          [domain: familyStats]
 * - Linguistic template collapse                               [domain: familyStats]
 *
 * ENHANCED (GAP-P4): Now checks FAMILY_SUPPORT_REGISTRY for actual hint presence,
 * not just emitting a generic info note.
 *
 * R7 (Sprint 5+): each check is now extracted into a named sub-validator
 * and wrapped with `runSubValidator` so one crash cannot blind the others.
 * Behaviour is otherwise identical to the previous inline implementation.
 *
 * EXPLANATION CLUSTER DEDUP (cleanup pass):
 *  - Schema owns "explanationText missing" (SCHEMA_MISSING_EXPLANATION, blocker).
 *  - Content owns "per-question explanationText too short" (CONTENT_EXPLANATION_TOO_SHORT).
 *  - Support owns "registry defaultExplanation missing/short/long" (different field).
 *  The previous SUPPORT_EXPLANATION_REQUIRED check was a 1-to-1 duplicate
 *  of SCHEMA_MISSING_EXPLANATION and was removed.
 *
 * ROLLUPS (Sprint 8):
 *  - defaultExplanation quality (missing/too_short/too_long) → SUPPORT_EXPLANATION_QUALITY
 *  - citoDifficulty spread (no_spread/low_spread) → SUPPORT_FAMILY_CITO_SPREAD
 *  Reason discriminator lives in metadata.reason. Same pattern as
 *  CONTENT_CITO_ISSUE / CONTENT_DISTRACTOR_QUALITY / DIVERSITY_TYPE_OVERUSE.
 *
 * REGISTRY-DRIVEN (Sprint 8):
 *  - The "math modules for number-pattern collapse" set is derived from
 *    `NUMERIC_CONTENT_MODULE_IDS` in src/content/modules.ts. Adding a new
 *    math-style module is a one-line change to the MODULES entry — no
 *    hardcoded set here to keep in sync.
 *
 * R6 — DOMAIN SPLIT (P1-2, mei 2026, partial):
 *  The 2026 governance refactor (see docs/audits/GOVERNANCE_DOMAIN_CONSOLIDATED_AUDIT_2026.md)
 *  called out that this stage was mixing three semantic concerns under domain 'support'.
 *  P1-2 splits the family-level statistical checks out into their own
 *  'familyStats' domain so the admin support-tab no longer lies about what
 *  it shows. The mapping is now:
 *    - support     → hint presence, visual-support coverage, registry
 *                    defaultExplanation hygiene (the real "support" concern)
 *    - familyStats → family question count, citoDifficulty spread,
 *                    number-pattern collapse, linguistic-template collapse
 *                    (statistical signals about a family as a whole)
 *  Note: the IssueCode strings (SUPPORT_FAMILY_LOW_QUESTION_COUNT, etc.)
 *  keep their SUPPORT_ prefix for back-compat with existing snapshots and
 *  the registry; only the `domain` field changed.
 */

import type { ValidationIssue } from '../issueContract'
import { coach, info } from '../issueContract'
import { getSupportPolicy, isPolicyFeatureEnabled } from '../policyEngine'
import { FAMILY_SUPPORT_REGISTRY } from '../../../support'
import { SUPPORT_THRESHOLDS } from '../../../support/policy'
import {
  QUESTIONS_BY_FAMILY,
  NUMERIC_CONTENT_MODULE_IDS,
  type Question,
} from '../../../content'
import {
  analyzeNumberPatterns,
  detectNumberPatternCollapse,
} from '../../utils/numberPatternAnalysis'
import {
  analyzeLinguisticVariation,
  detectLinguisticTemplateCollapse,
  getLinguisticVariationSuggestions,
} from '../../utils/linguisticVariationAnalysis'
import { runSubValidator } from './runSubValidator'
import { SEVERITY } from '../severityScale'
import { STAGES } from '../stageDefinitions'

// SSOT for stage numbers — see stageDefinitions.ts (G-16 fix, RC-3)
const STAGE = STAGES.SUPPORT

// ─── Helper: emit-once-per-family anchor ─────────────────────────────────────
//
// Several family-level checks (cito-spread, number-pattern-collapse,
// linguistic-template-collapse) should only fire ONCE per family, not once
// per question. The convention is to fire on the first question in the
// family's QUESTIONS_BY_FAMILY array (deterministic load order). This helper
// centralises the check so the three sub-validators stay readable.

function isFamilyAnchor(question: Question): boolean {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId!)
  return familyQuestions?.[0]?.id === question.id
}

// ─── Sub-validator: hints required ───────────────────────────────────────────

function checkHintsRequired(
  question: Question,
  supportPolicy: ReturnType<typeof getSupportPolicy>,
): ValidationIssue[] {
  if (!supportPolicy?.hintsRequired) return []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const familyId = question.familyId!

  const registryEntry = FAMILY_SUPPORT_REGISTRY[familyId]
  const hasRegistryHints =
    registryEntry?.hints && registryEntry.hints.length > 0

  if (!registryEntry) {
    return [
      coach(
        'support',
        STAGE,
        'SUPPORT_FAMILY_NOT_IN_REGISTRY',
        `Family "${familyId}" requires hints but is not in FAMILY_SUPPORT_REGISTRY`,
        {
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_MEDIUM,
          suggestion:
            'Add this family to FAMILY_SUPPORT_REGISTRY with appropriate hints',
        },
      ),
    ]
  }
  if (!hasRegistryHints) {
    return [
      coach(
        'support',
        STAGE,
        'SUPPORT_HINTS_EMPTY',
        `Family "${familyId}" is in registry but has no hints defined`,
        {
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
          suggestion:
            'Add hints array with at least one HintStep to the registry entry',
        },
      ),
    ]
  }
  return []
}

// ─── Sub-validator: defaultExplanation quality (ROLLUP) ──────────────────────
//
// Sprint 8: three legacy codes (SUPPORT_EXPLANATION_MISSING / _TOO_SHORT /
// _TOO_LONG) merged into one rollup code SUPPORT_EXPLANATION_QUALITY with
// `metadata.reason` as the discriminator. Admin UI groups primary by code,
// secondary by reason.

type ExplanationReason = 'missing' | 'too_short' | 'too_long'

function checkDefaultExplanation(question: Question): ValidationIssue[] {
  const familyId = question.familyId!
  const registryEntry = FAMILY_SUPPORT_REGISTRY[familyId]
  if (!registryEntry) return []
  const qId = question.id ?? 'UNKNOWN'
  const fId = familyId
  const explanation = registryEntry.defaultExplanation?.trim() ?? ''
  const len = explanation.length

  let reason: ExplanationReason | null = null
  if (!explanation) reason = 'missing'
  else if (len < SUPPORT_THRESHOLDS.MIN_EXPLANATION_CHARS) reason = 'too_short'
  else if (len > SUPPORT_THRESHOLDS.MAX_EXPLANATION_CHARS) reason = 'too_long'
  if (reason === null) return []

  if (reason === 'missing') {
    return [
      coach(
        'support',
        STAGE,
        'SUPPORT_EXPLANATION_QUALITY',
        `Family "${familyId}" is in registry but has no defaultExplanation`,
        {
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
          suggestion:
            'Add a defaultExplanation string that explains the concept in 1-3 sentences suitable for groep 6/7/8',
          metadata: { reason, length: len },
        },
      ),
    ]
  }
  if (reason === 'too_short') {
    return [
      coach(
        'support',
        STAGE,
        'SUPPORT_EXPLANATION_QUALITY',
        `Family "${familyId}" has a very short defaultExplanation (${len} chars) — likely incomplete`,
        {
          field: 'defaultExplanation',
          expected: `≥${SUPPORT_THRESHOLDS.MIN_EXPLANATION_CHARS} chars`,
          actual: `${len} chars`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_LOW,
          suggestion: `Expand the defaultExplanation to at least ${SUPPORT_THRESHOLDS.MIN_EXPLANATION_CHARS} characters with a clear, child-friendly explanation of the concept`,
          metadata: { reason, length: len },
        },
      ),
    ]
  }
  // reason === 'too_long'
  return [
    info(
      'support',
      STAGE,
      'SUPPORT_EXPLANATION_QUALITY',
      `Family "${familyId}" has a long defaultExplanation (${len} chars) — may be hard to read for groep 6/7/8`,
      {
        field: 'defaultExplanation',
        expected: `≤${SUPPORT_THRESHOLDS.MAX_EXPLANATION_CHARS} chars`,
        actual: `${len} chars`,
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.COSMETIC,
        suggestion: `Consider shortening the defaultExplanation to ≤${SUPPORT_THRESHOLDS.MAX_EXPLANATION_CHARS} characters for better readability`,
        metadata: { reason, length: len },
      },
    ),
  ]
}

// ─── Sub-validator: visual support required ──────────────────────────────────

function checkVisualSupportRequired(
  question: Question,
  supportPolicy: ReturnType<typeof getSupportPolicy>,
): ValidationIssue[] {
  if (!supportPolicy?.visualSupportRequired) return []
  const hasVisualSupport =
    Array.isArray(question.supportAids) && question.supportAids.length > 0
  if (hasVisualSupport) return []
  return [
    coach(
      'support',
      STAGE,
      'SUPPORT_VISUAL_REQUIRED',
      `Family "${question.familyId}" requires visual support aids`,
      {
        field: 'supportAids',
        questionId: question.id ?? 'UNKNOWN',
        familyId: question.familyId ?? 'UNKNOWN',
        severity: SEVERITY.MEDIUM,
      },
    ),
  ]
}

// ─── Sub-validator: family question count (CAL-004) ──────────────────────────
//
// P1-2 (mei 2026): moved from domain 'support' to 'familyStats' — this is a
// family-level statistical signal, not a support-coverage concern.

function checkFamilyQuestionCount(question: Question): ValidationIssue[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId!)
  const familyCount = familyQuestions?.length ?? 0
  if (familyCount >= SUPPORT_THRESHOLDS.MIN_QUESTIONS_PER_FAMILY) return []
  return [
    info(
      'familyStats',
      STAGE,
      'SUPPORT_FAMILY_LOW_QUESTION_COUNT',
      `Family "${question.familyId}" has only ${familyCount} question(s) — minimum ${SUPPORT_THRESHOLDS.MIN_QUESTIONS_PER_FAMILY} recommended for adequate practice coverage`,
      {
        field: 'familyId',
        expected: `≥${SUPPORT_THRESHOLDS.MIN_QUESTIONS_PER_FAMILY}`,
        actual: String(familyCount),
        questionId: question.id ?? 'UNKNOWN',
        familyId: question.familyId ?? 'UNKNOWN',
        severity: SEVERITY.NEAR_MINOR,
        suggestion: `Author additional questions for family "${question.familyId}" to reach at least ${SUPPORT_THRESHOLDS.MIN_QUESTIONS_PER_FAMILY} for balanced practice sessions`,
      },
    ),
  ]
}

// ─── Sub-validator: citoDifficulty spread within family (F3, ROLLUP) ─────────
//
// Sprint 8: two legacy codes (SUPPORT_FAMILY_CITO_NO_SPREAD,
// SUPPORT_FAMILY_CITO_LOW_SPREAD) merged into one rollup code
// SUPPORT_FAMILY_CITO_SPREAD with `metadata.reason` as the discriminator.
//
// P1-2 (mei 2026): moved from domain 'support' to 'familyStats'.

type CitoSpreadReason = 'no_spread' | 'low_spread'

function checkCitoSpread(question: Question): ValidationIssue[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId!)
  if (
    !familyQuestions ||
    familyQuestions.length < SUPPORT_THRESHOLDS.MIN_FAMILY_SIZE_FOR_CITO_SPREAD
  )
    return []
  if (question.citoDifficulty === undefined) return []
  // Family-level signal — emit once per family on the anchor question only.
  if (!isFamilyAnchor(question)) return []

  const citoDiffs = familyQuestions
    .map((q) => q.citoDifficulty)
    .filter((d): d is number => d !== undefined && typeof d === 'number')
  if (citoDiffs.length < SUPPORT_THRESHOLDS.MIN_FAMILY_SIZE_FOR_CITO_SPREAD)
    return []

  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  const allSame = citoDiffs.every((d) => d === citoDiffs[0])
  const minDiff = Math.min(...citoDiffs)
  const maxDiff = Math.max(...citoDiffs)
  const spread = maxDiff - minDiff

  let reason: CitoSpreadReason | null = null
  if (allSame) reason = 'no_spread'
  else if (spread < SUPPORT_THRESHOLDS.MIN_CITO_SPREAD) reason = 'low_spread'
  if (reason === null) return []

  if (reason === 'no_spread') {
    return [
      info(
        'familyStats',
        STAGE,
        'SUPPORT_FAMILY_CITO_SPREAD',
        `All ${citoDiffs.length} questions in family "${question.familyId}" have identical citoDifficulty (${citoDiffs[0]}) — spread recommended for balanced assessment`,
        {
          field: 'citoDifficulty',
          expected: `spread ≥ ${SUPPORT_THRESHOLDS.MIN_CITO_SPREAD.toFixed(2)}`,
          actual: `all ${citoDiffs[0]}`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.MINOR,
          suggestion:
            'Vary citoDifficulty across questions (e.g., 0.25-0.55) to create a mix of easier and harder items',
          metadata: {
            reason,
            spread,
            min: minDiff,
            max: maxDiff,
            sampleSize: citoDiffs.length,
          },
        },
      ),
    ]
  }
  // reason === 'low_spread'
  return [
    coach(
      'familyStats',
      STAGE,
      'SUPPORT_FAMILY_CITO_SPREAD',
      `Family "${question.familyId}" has insufficient citoDifficulty spread (${spread.toFixed(2)}, range: ${minDiff}-${maxDiff}) — all questions are too similar in difficulty`,
      {
        field: 'citoDifficulty',
        expected: `spread ≥ ${SUPPORT_THRESHOLDS.MIN_CITO_SPREAD.toFixed(2)}`,
        actual: `spread ${spread.toFixed(2)}`,
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.LOW,
        suggestion:
          'Add both "instap" questions (lower difficulty) and "uitdaging" questions (higher difficulty) to create a learning progression within the family',
        metadata: {
          reason,
          spread,
          min: minDiff,
          max: maxDiff,
          sampleSize: citoDiffs.length,
        },
      },
    ),
  ]
}

// ─── Sub-validator: number pattern collapse (F6) ─────────────────────────────
//
// Registry-driven (Sprint 8): the set of "math-style" modules eligible for
// number-pattern analysis comes from NUMERIC_CONTENT_MODULE_IDS in
// src/content/modules.ts. To add a math-style module: set
// `hasNumericContent: true` on its MODULES entry — no edit needed here.
//
// P1-2 (mei 2026): moved from domain 'support' to 'familyStats'.

function checkNumberPatternCollapse(question: Question): ValidationIssue[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId!)
  if (
    !familyQuestions ||
    familyQuestions.length <
      SUPPORT_THRESHOLDS.MIN_FAMILY_SIZE_FOR_PATTERN_ANALYSIS
  )
    return []
  if (!isFamilyAnchor(question)) return []
  if (!question.module || !NUMERIC_CONTENT_MODULE_IDS.has(question.module))
    return []

  const analysis = analyzeNumberPatterns(
    familyQuestions.map((q) => ({
      question: q.question ?? '',
      options: q.options,
    })),
  )
  const collapse = detectNumberPatternCollapse(analysis)
  if (!collapse.issue) return []
  return [
    info(
      'familyStats',
      STAGE,
      'SUPPORT_FAMILY_NUMBER_PATTERN_COLLAPSE',
      `Family "${question.familyId}" shows number pattern collapse: ${collapse.issue}`,
      {
        field: 'question',
        questionId: question.id ?? 'UNKNOWN',
        familyId: question.familyId ?? 'UNKNOWN',
        severity: collapse.severity,
        suggestion:
          'Add questions with "ugly" numbers (13, 27, 3.47), edge cases (0, 1), and overflow situations (18+5) to prevent model collapse',
      },
    ),
  ]
}

// ─── Sub-validator: linguistic template collapse (F7) ────────────────────────
//
// P1-2 (mei 2026): moved from domain 'support' to 'familyStats'.

function checkLinguisticTemplateCollapse(
  question: Question,
): ValidationIssue[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(question.familyId!)
  if (
    !familyQuestions ||
    familyQuestions.length <
      SUPPORT_THRESHOLDS.MIN_FAMILY_SIZE_FOR_PATTERN_ANALYSIS
  )
    return []
  if (!isFamilyAnchor(question)) return []

  const lingAnalysis = analyzeLinguisticVariation(
    familyQuestions.map((q) => ({ question: q.question ?? '' })),
  )
  const lingCollapse = detectLinguisticTemplateCollapse(lingAnalysis)
  if (!lingCollapse.issue) return []
  const suggestions = getLinguisticVariationSuggestions(lingAnalysis)
  return [
    info(
      'familyStats',
      STAGE,
      'SUPPORT_FAMILY_LINGUISTIC_TEMPLATE_COLLAPSE',
      `Family "${question.familyId}" shows linguistic template collapse: ${lingCollapse.issue}`,
      {
        field: 'question',
        questionId: question.id ?? 'UNKNOWN',
        familyId: question.familyId ?? 'UNKNOWN',
        severity: lingCollapse.severity,
        suggestion: suggestions.join(' • '),
      },
    ),
  ]
}

// ─── Stage Entry Point ───────────────────────────────────────────────────────

/**
 * Run support validation on a question.
 *
 * R7 (Sprint 5+): each sub-check is crash-isolated. The early-out guards
 * (no familyId, feature flag off, no policy) remain at the entry point so
 * we don't wrap pure short-circuits in try/catch.
 *
 * P1-2 (mei 2026): four sub-validators below run under domain 'familyStats'
 * rather than 'support' (see header comment for the split rationale).
 */
export function runSupportStage(question: Question): ValidationIssue[] {
  if (!question.familyId) return []
  if (!isPolicyFeatureEnabled('enforceSupportCoverage')) return []

  const supportPolicy = getSupportPolicy(question.familyId)
  if (!supportPolicy) return []

  return [
    ...runSubValidator('checkHintsRequired', 'support', STAGE, () =>
      checkHintsRequired(question, supportPolicy),
    ),
    ...runSubValidator('checkDefaultExplanation', 'support', STAGE, () =>
      checkDefaultExplanation(question),
    ),
    ...runSubValidator('checkVisualSupportRequired', 'support', STAGE, () =>
      checkVisualSupportRequired(question, supportPolicy),
    ),
    ...runSubValidator('checkFamilyQuestionCount', 'familyStats', STAGE, () =>
      checkFamilyQuestionCount(question),
    ),
    ...runSubValidator('checkCitoSpread', 'familyStats', STAGE, () =>
      checkCitoSpread(question),
    ),
    ...runSubValidator('checkNumberPatternCollapse', 'familyStats', STAGE, () =>
      checkNumberPatternCollapse(question),
    ),
    ...runSubValidator(
      'checkLinguisticTemplateCollapse',
      'familyStats',
      STAGE,
      () => checkLinguisticTemplateCollapse(question),
    ),
  ]
}
